/**
 * 제품 정보 카드 (D-2) — 상세 최상단.
 *
 * 약사가 룰 숫자를 허가사항 원문과 대조하려면 제품이 무엇이고 1단위가 몇 mg인지 알아야 한다.
 * 서버 응답(targetProduct·productRef·checks)에 이미 있는 값을 결정적으로 그린다. 저장하지 않는
 * 기준 성분은 identity에서 D-7 규칙으로 복원한다. 순수 프리젠테이션 — 데이터 fetch 없음(ARCHITECTURE §2).
 *
 * 값 없음 3종(D-5): 미제공(서버 필드 없음) / 미기록(옛 추출) / 해당 없음(null이 의미).
 * selectors·sharedSkus·companyName은 Phase 2 필드라 이번 Phase 응답엔 없어 "미제공"으로 보인다.
 */
import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import {
  DoseBaked,
  DoseProductIngredient,
  DoseProductRef,
  DoseRuleSelector,
  DoseSharedSku,
  DoseTargetProduct,
} from "services/api/doseRules/types";
import {
  NOT_APPLICABLE_TEXT,
  NOT_PROVIDED_TEXT,
  doseCheckEligibleLabel,
  doseIneligibleReasonLabel,
  readGateIssues,
  restoreBasisIngredient,
} from "utils/doseRule";

export interface DoseProductInfoCardProps {
  targetProduct: DoseTargetProduct | null;
  productRef: DoseProductRef | null;
  /** 대상 제품 dose_products.checks (제품 검산 — D-2 row10). */
  checks: Record<string, unknown> | null;
  /** 이 룰이 걸리는 selector(D-6, Phase 2). 없으면 미제공. */
  selectors?: DoseRuleSelector[];
  /** 같은 보험코드 공유 SKU(D-6, Phase 2). 없으면 미제공. */
  sharedSkus?: DoseSharedSku[];
  sharedSkusTruncated?: boolean;
  /** 업체명(D-6, Phase 2). 없으면 미제공. */
  companyName?: string | null;
  /** 전개본 환산비(D-6, Phase 2). 있으면 "대표 1{from}=이 제품 {ratio}{to}". */
  baked?: DoseBaked | null;
}

const Card = styled.section`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  margin-bottom: 2px;
`;

const ProductName = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  word-break: break-all;
`;

const CompanyName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-left: 8px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 16px;
  margin-top: 10px;
  font-size: 13px;
`;

const Full = styled.div`
  grid-column: 1 / -1;
`;

const Field = styled.div`
  display: flex;
  gap: 6px;
  align-items: baseline;
`;

const Key = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  min-width: 72px;
  flex-shrink: 0;
`;

const Value = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 600;
  word-break: break-all;
`;

const SubText = styled.div`
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin-top: 2px;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  margin: 10px 0 4px;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const CheckBox = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const CheckIssue = styled.span`
  color: ${COLOR_SYSTEM.get("Red")?.[700]};
`;

const EmptyNote = styled.div`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 8px;
`;

const Fold = styled.details`
  margin-top: 6px;
  summary {
    cursor: pointer;
    font-size: 13px;
    color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  }
`;

/** targetProduct 필드(응답엔 있고 값만 null): null이 의미 있으므로 "해당 없음". */
const codeVal = (v: string | null | undefined): string =>
  typeof v === "string" && v.trim() ? v : NOT_APPLICABLE_TEXT;

/** 성분 한 줄의 대표 함량 행(D-7 규칙: is_ud_basis ?? 첫 행). */
const ingredientStrengthText = (ing: DoseProductIngredient): string => {
  const strengths = Array.isArray(ing?.strengths) ? ing.strengths : [];
  const st = strengths.find((s) => s?.is_ud_basis) ?? strengths[0];
  if (!st) return NOT_APPLICABLE_TEXT;
  const amount =
    st.amount && typeof st.amount.value === "number"
      ? `${st.amount.value}${st.amount.unit ?? ""}`
      : null;
  const per =
    st.per && typeof st.per.value === "number" ? `${st.per.value}${st.per.unit ?? ""}` : null;
  if (amount && per) return `${amount}/${per}`;
  return amount ?? NOT_APPLICABLE_TEXT;
};

const KV: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Field>
    <Key>{label}</Key>
    <Value>{children}</Value>
  </Field>
);

const DoseProductInfoCard: React.FC<DoseProductInfoCardProps> = ({
  targetProduct,
  productRef,
  checks,
  selectors,
  sharedSkus,
  sharedSkusTruncated,
  companyName,
  baked,
}) => {
  const productName = productRef?.product_name || null;
  const companyText =
    companyName === undefined
      ? NOT_PROVIDED_TEXT
      : companyName && companyName.trim()
      ? companyName
      : NOT_APPLICABLE_TEXT;

  // targetProduct가 없는 룰(빈 상태): productRef만 표시(화면 설계 3 — 빈).
  if (!targetProduct) {
    return (
      <Card data-testid="dose-product-info-card">
        <CardTitle>제품 정보</CardTitle>
        <EmptyNote>
          이 룰에 연결된 제품 정보가 없습니다 — 룰의 제품 참조(productRef)만 표시합니다.
        </EmptyNote>
        <ProductName>{productName || NOT_APPLICABLE_TEXT}</ProductName>
        <Grid>
          <KV label="품목기준코드">{codeVal(productRef?.item_code)}</KV>
          <KV label="표준코드">{codeVal(productRef?.standard_code)}</KV>
          <KV label="처방단위">{codeVal(productRef?.prescribe_unit)}</KV>
        </Grid>
      </Card>
    );
  }

  const tp = targetProduct;
  const dispense = tp.dispense ?? null;
  const perUnit = dispense?.per_unit ?? null;
  const perPack = dispense?.per_pack ?? null;
  const { basis, salt } = restoreBasisIngredient(tp.identity);
  const ingredients = Array.isArray(tp.identity?.ingredients) ? tp.identity?.ingredients ?? [] : [];
  const gateIssues = readGateIssues(checks);
  const mainIngredient = (checks?.main_ingredient ?? null) as { verdict?: string } | null;
  const citationsGrade =
    typeof checks?.citations_grade === "string" ? (checks.citations_grade as string) : null;
  const anchorMissing = checks?.anchor_missing === true;

  // 1단위 함량 — "200mg/정" + 기준 성분·염(D-7).
  const perUnitText =
    perUnit && typeof perUnit.value === "number"
      ? `${perUnit.value}${perUnit.unit ?? ""}`
      : NOT_APPLICABLE_TEXT;

  const prescribeUnit = codeVal(dispense?.prescribe_unit ?? productRef?.prescribe_unit);

  return (
    <Card data-testid="dose-product-info-card">
      <CardTitle>제품 정보</CardTitle>
      <ProductName>
        {productName || NOT_APPLICABLE_TEXT}
        <CompanyName>{companyText}</CompanyName>
      </ProductName>

      <Grid>
        {/* row2·3 — 코드 6종 + 기관 코드 */}
        <KV label="표준코드">{codeVal(tp.standardCode ?? productRef?.standard_code)}</KV>
        <KV label="보험코드">{codeVal(tp.insuranceCode)}</KV>
        <KV label="품목기준코드">{codeVal(tp.itemCode ?? productRef?.item_code)}</KV>
        <KV label="대표코드">{codeVal(tp.mainCode)}</KV>
        <KV label="성분코드">{codeVal(tp.ingredientCode)}</KV>
        <KV label="ATC">{codeVal(tp.atcCode)}</KV>
        <KV label="기관 코드">{codeVal(tp.productCode)}</KV>

        {/* row4 — 처방단위·1단위 함량·계산식·1포장 */}
        <KV label="처방단위">{prescribeUnit}</KV>
        <Field>
          <Key>1단위 함량</Key>
          <div>
            <Value data-testid="dose-per-unit">{perUnitText}</Value>
            <SubText>
              기준 {basis ?? NOT_APPLICABLE_TEXT} · 염 {salt ?? NOT_APPLICABLE_TEXT}
            </SubText>
          </div>
        </Field>
        {perUnit?.chain ? (
          <Full>
            <Field>
              <Key>계산식</Key>
              <Value style={{ fontWeight: 500 }}>{perUnit.chain}</Value>
            </Field>
          </Full>
        ) : null}
        {perPack && typeof perPack.value === "number" ? (
          <KV label="1포장">
            {perPack.value}
            {perPack.unit ?? ""}
          </KV>
        ) : null}

        {/* row5 — 제형·경로 */}
        <KV label="제형">{codeVal(tp.dosageForm)}</KV>
        <KV label="투여경로">{codeVal(tp.route)}</KV>

        {/* row6 — 판정 대상 여부·제외 사유 */}
        <KV label="판정 대상 여부">
          {doseCheckEligibleLabel(tp.doseCheckEligible)}
          {tp.doseCheckIneligibleReason
            ? ` · 제외: ${doseIneligibleReasonLabel(tp.doseCheckIneligibleReason)}`
            : ""}
        </KV>

        {/* 전개본 환산비(D-6, Phase 2) */}
        {baked ? (
          <KV label="환산비">
            대표 1{baked.from_unit} = 이 제품 {baked.ratio}
            {baked.to_unit}
          </KV>
        ) : null}
      </Grid>

      {/* row7 — 성분별 함량 */}
      <SectionLabel>성분별 함량</SectionLabel>
      {ingredients.length > 0 ? (
        <List>
          {ingredients.map((ing, i) => (
            <li key={i} data-testid="dose-ingredient-row">
              {ing.core_ingredient ?? NOT_APPLICABLE_TEXT}
              {ing.salt ? ` (${ing.salt})` : ""} — {ingredientStrengthText(ing)}
            </li>
          ))}
        </List>
      ) : (
        <SubText>{NOT_APPLICABLE_TEXT}</SubText>
      )}

      {/* row8 — 이 룰이 걸리는 코드(selectors, Phase 2) */}
      <SectionLabel>이 룰이 걸리는 코드</SectionLabel>
      {selectors === undefined ? (
        <SubText>{NOT_PROVIDED_TEXT}</SubText>
      ) : selectors.length === 0 ? (
        <SubText>{NOT_APPLICABLE_TEXT}</SubText>
      ) : (
        <List>
          {selectors.map((s, i) => (
            <li key={i}>
              {s.label} {s.value}
            </li>
          ))}
        </List>
      )}

      {/* row9 — 같은 보험코드 제품(sharedSkus, Phase 2) */}
      <SectionLabel>같은 보험코드 제품</SectionLabel>
      {sharedSkus === undefined ? (
        <SubText>{NOT_PROVIDED_TEXT}</SubText>
      ) : sharedSkus.length === 0 ? (
        <SubText>{NOT_APPLICABLE_TEXT}</SubText>
      ) : (
        <Fold>
          <summary>
            {sharedSkus.length}개{sharedSkusTruncated ? " (200개까지만 표시)" : ""}
          </summary>
          <List>
            {sharedSkus.map((s, i) => (
              <li key={i}>
                {s.standardCode}
                {s.productName ? ` · ${s.productName}` : ""}
              </li>
            ))}
          </List>
        </Fold>
      )}

      {/* row10 — 제품 검산 */}
      <CheckBox>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>제품 검산</div>
        <div>인용 등급: {citationsGrade ?? NOT_APPLICABLE_TEXT}</div>
        <div>주성분 검산: {mainIngredient?.verdict ?? NOT_APPLICABLE_TEXT}</div>
        <div>앵커 누락: {anchorMissing ? "예" : "아니오"}</div>
        {gateIssues.length > 0 ? (
          <div>
            검산 이슈:{" "}
            {gateIssues.map((issue, i) => (
              <CheckIssue key={i}>
                {i > 0 ? " · " : ""}
                {issue}
              </CheckIssue>
            ))}
          </div>
        ) : (
          <div>검산 이슈: 없음</div>
        )}
      </CheckBox>
    </Card>
  );
};

export default DoseProductInfoCard;
